import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Switch,
  InputNumber,
  Image,
  Tag,
  Popconfirm,
  Spin,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";
import {
  useInsightDelete,
  useInsightList,
  useInsightSave,
} from "../../../hooks/api/insight/hooks";
import { InsightPayload } from "../../../types/api/insight";

const InsightListing = ({ allowURLStateUpdate = true }) => {
  const [messageApi, contextHolder] = message.useMessage();

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useInsightList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const insightList = data?.data ?? [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveInsight, isPending } = useInsightSave({ qData });
  const { mutate: deleteInsight } = useInsightDelete({ qData });

  const [editingInsight, setEditingInsight] = useState<InsightPayload | null>(
    null,
  );
  const [editing, setEditing] = useState(false);

  const [form] = Form.useForm();

  const handleDelete = (id: string) => {
    deleteInsight(
      { id },
      {
        onSuccess: () => {
          messageApi.success("Insight deleted successfully");
          refetch({ page: qData.page, limit: qData.limit });
        },
        onError: () => {
          messageApi.error("Failed to delete insight");
        },
      },
    );
  };

  const handleCancel = () => {
    setEditingInsight(null);
    setEditing(false);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload = {
        _id: editingInsight?._id,
        heading: values.heading,
        subtext: values.subtext,
        image: values.image,
        linkedIn: values.linkedIn,
        order: values.order ?? 0,
        status: values.status ?? false,
        isDeleted: false,
      };

      saveInsight(payload, {
        onSuccess: () => {
          messageApi.success("Insight saved successfully");
          refetch({ page: qData.page, limit: qData.limit, _ts: Date.now() });
          handleCancel();
        },
        onError: () => {
          messageApi.error("Failed to save insight");
        },
      });
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  useEffect(() => {
    if (editingInsight) {
      form.setFieldsValue({
        heading: editingInsight.heading,
        subtext: editingInsight.subtext,
        image: editingInsight.image,
        linkedIn: editingInsight.linkedIn,
        order: editingInsight.order,
        status: editingInsight.status,
      });
    }
  }, [editingInsight, form]);

  const handleEditClick = (item: InsightPayload) => {
    setEditingInsight(item);
    setEditing(true);
  };

  const handleAddClick = () => {
    setEditingInsight(null);
    form.resetFields();
    setEditing(true);
  };

  return (
    <div>
      {contextHolder}

      {editing ? (
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-2xl shadow-sm mb-6">
            <Form
              layout="vertical"
              form={form}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Form.Item
                label="Heading"
                name="heading"
                rules={[{ required: true, message: "Heading is required" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item label="LinkedIn URL" name="linkedIn">
                <Input placeholder="https://linkedin.com/..." />
              </Form.Item>

              <Form.Item
                label="Subtext"
                name="subtext"
                className="sm:col-span-2"
              >
                <TextArea rows={3} placeholder="Enter subtext" />
              </Form.Item>

              <Form.Item label="Status" name="status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Form.Item label="Order" name="order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Image"
                name="image"
                rules={[{ required: true, message: "Image is required" }]}
                className="sm:col-span-2"
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isPending}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden sm:block rounded-[20px] overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Image</th>
                  <th className="py-3 px-4 text-left">Heading</th>
                  <th className="py-3 px-4 text-left">Subtext</th>
                  <th className="py-3 px-4 text-left">Order</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {insightList.length > 0 ? (
                  <>
                    {insightList.map((item) => (
                      <tr key={item._id} className="bg-[#F9F9F9] border-b">
                        <td className="py-3 px-4">
                          <Image
                            src={item.image}
                            width={60}
                            height={60}
                            fallback="/placeholder.jpg"
                          />
                        </td>
                        <td className="py-3 px-4">{item.heading}</td>
                        <td className="py-3 px-4">{item.subtext}</td>
                        <td className="py-3 px-4">{item.order}</td>
                        <td className="py-3 px-4">
                          <Tag color={item.status ? "green" : "red"}>
                            {item.status ? "Active" : "Inactive"}
                          </Tag>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex">
                            <EditOutlined
                              onClick={() => handleEditClick(item)}
                              className="mr-4 text-primary text-[20px]"
                            />
                            <Popconfirm
                              title="Delete this insight?"
                              onConfirm={() => item?._id && handleDelete(item?._id)}
                            >
                              <DeleteOutlined className="text-red-500 text-[20px]" />
                            </Popconfirm>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                ) : (
                  <tr>
                    <td colSpan={6} className="py-3 px-4 text-center bg-[#f9f9f9] text-gray-700">No Insight Found</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div className="p-3 text-right bg-[#F9F9F9]">
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddClick}
                type="dashed"
                className="text-primary font-semibold"
              >
                Add Insight
              </Button>
            </div>
          </div>

          <div className="sm:hidden space-y-4">
            {insightList.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-xl shadow p-4 flex gap-3"
              >
                <Image
                  src={item.image}
                  width={70}
                  height={70}
                  fallback="/placeholder.jpg"
                />

                <div className="flex-1">
                  <h3 className="font-semibold">{item.heading}</h3>
                  <p className="text-sm text-gray-500">{item.subtext}</p>
                  <Tag color={item.status ? "green" : "red"}>
                    {item.status ? "Active" : "Inactive"}
                  </Tag>

                  <div className="flex gap-4 mt-2 text-lg">
                    <EditOutlined onClick={() => handleEditClick(item)} className="text-primary text-[20px]" />
                    <Popconfirm
                      title="Delete this insight?"
                      onConfirm={() => item._id && handleDelete(item._id)}
                    >
                      <DeleteOutlined className="text-red-500" />
                    </Popconfirm>
                  </div>
                </div>
              </div>
            ))}

            <Button
              icon={<PlusOutlined />}
              onClick={handleAddClick}
              type="dashed"
              block
              className="text-primary font-semibold"
            >
              Add Insight
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default InsightListing;
